import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { runFullDiagnostics } from '../utils/healthCheck';
import { useAnalytics } from '../contexts/AnalyticsContext';

const { FiActivity, FiDatabase, FiCheckCircle, FiRefreshCw, FiServer, FiCpu, FiGlobe, FiShield, FiLock, FiTerminal, FiRadio } = FiIcons;

const PlatformHealth = () => {
  const { analyticsData } = useAnalytics();
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hostSite, setHostSite] = useState('Detecting...');

  const performCheck = async () => {
    setLoading(true);
    const data = await runFullDiagnostics();
    setHealth(data);
    setLoading(false);
    
    if (typeof window !== 'undefined') {
      setHostSite(window.location.host);
    }
  };

  useEffect(() => {
    performCheck();
  }, []);

  return (
    <div className="space-y-8">
      {/* Tracked Origin Confirmation */}
      <div className="bg-white p-8 rounded-3xl border border-slate-200 shadow-sm flex flex-col md:flex-row justify-between items-center gap-6">
        <div className="flex items-center gap-5">
          <div className="w-14 h-14 bg-blue-600 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-blue-200">
            <SafeIcon icon={FiGlobe} className="text-2xl" />
          </div>
          <div>
            <h2 className="text-xl font-black text-slate-900 tracking-tight">Surveillance Origin</h2>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-sm font-mono text-blue-600 font-bold bg-blue-50 px-2.5 py-1 rounded-lg">
                {hostSite}
              </span>
              <span className="flex items-center gap-1 text-[10px] font-black text-emerald-600 uppercase tracking-widest bg-emerald-50 px-2 py-1 rounded-lg border border-emerald-100">
                <SafeIcon icon={FiCheckCircle} /> Verified
              </span>
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-1">Environment</div>
          <div className="text-sm font-black text-slate-900 flex items-center gap-2 justify-end">
             <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
             PRODUCTION_LIVE
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <HealthCard 
          icon={FiDatabase} 
          title="Telemetry Storage" 
          status={health?.database.status} 
          detail="Supabase Real-time Cluster" 
          metric={`${health?.database.latency || 0}ms`} 
        />
        <HealthCard 
          icon={FiLock} 
          title="Data Sanitization" 
          status="healthy" 
          detail="Zero-PII Payload Validation" 
          metric="ENABLED" 
        />
        <HealthCard 
          icon={FiCpu} 
          title="Neural Pipeline" 
          status="healthy" 
          detail="Active Event Buffer" 
          metric={`${analyticsData.pageViews.length + analyticsData.events.length} Signals`} 
        />
      </div>

      {/* Live Signal Debugger */}
      <div className="bg-slate-900 rounded-[2.5rem] p-10 text-white shadow-2xl">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-600 rounded-2xl flex items-center justify-center shadow-xl shadow-blue-900/50">
              <SafeIcon icon={FiTerminal} />
            </div>
            <div>
              <h3 className="text-lg font-bold">Live Signal Monitor</h3>
              <p className="text-slate-400 text-xs font-medium uppercase tracking-widest">Real-time incoming telemetry log</p>
            </div>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/20 rounded-xl">
             <div className="w-2 h-2 bg-blue-400 rounded-full animate-ping"></div>
             <span className="text-[10px] font-black text-blue-400 uppercase tracking-widest">Listening</span>
          </div>
        </div>

        <div className="space-y-4 max-h-64 overflow-y-auto custom-scrollbar pr-4">
          {analyticsData.pageViews.length === 0 && analyticsData.events.length === 0 ? (
            <div className="py-12 text-center text-slate-500 italic font-medium">
              No signals detected yet. Deploy the script to start monitoring.
            </div>
          ) : (
            [...analyticsData.pageViews, ...analyticsData.events]
              .sort((a,b) => b.timestamp - a.timestamp)
              .slice(0, 10)
              .map((signal, idx) => (
                <motion.div 
                  key={idx}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="flex items-center justify-between p-4 bg-white/5 border border-white/10 rounded-2xl"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-8 h-8 bg-blue-500/20 text-blue-400 rounded-lg flex items-center justify-center">
                      <SafeIcon icon={signal.eventType === 'pageview' ? FiGlobe : FiRadio} className="text-sm" />
                    </div>
                    <div>
                      <div className="text-xs font-black text-white uppercase tracking-wider">
                        {signal.eventType === 'pageview' ? 'PAGE_VIEW' : signal.eventName}
                      </div>
                      <div className="text-[10px] text-slate-400 font-mono mt-0.5">
                        {signal.page || signal.url}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] font-black text-blue-400">{new Date(signal.timestamp).toLocaleTimeString()}</div>
                    <div className="text-[9px] text-slate-500 font-mono mt-0.5">VID: {signal.visitorId.substring(0,8)}</div>
                  </div>
                </motion.div>
              ))
          )}
        </div>
      </div>

      <div className="flex justify-center pt-6">
        <button 
          onClick={performCheck} 
          className="flex items-center gap-3 px-8 py-4 bg-white border border-slate-200 rounded-2xl text-[10px] font-black uppercase tracking-widest text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-all shadow-sm hover:shadow-xl group"
        >
          <SafeIcon icon={FiRefreshCw} className={`text-sm ${loading ? 'animate-spin' : 'group-hover:rotate-180 transition-transform duration-500'}`} />
          Run Global Diagnostics
        </button>
      </div>
    </div>
  );
};

const HealthCard = ({ icon, title, status, detail, metric }) => (
  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white p-8 rounded-[2rem] border border-slate-100 shadow-sm hover:shadow-xl transition-all">
    <div className="flex justify-between items-start mb-6">
      <div className="w-12 h-12 bg-slate-50 rounded-2xl flex items-center justify-center text-blue-600 border border-slate-100">
        <SafeIcon icon={icon} className="text-xl" />
      </div>
      <div className={`px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest ${
        status === 'healthy' ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' : 'bg-rose-50 text-rose-600 border border-rose-100'
      }`}>
        {status || 'checking'}
      </div>
    </div>
    <h4 className="font-black text-slate-900 text-base">{title}</h4>
    <p className="text-xs text-slate-500 font-medium mt-1 mb-6 leading-relaxed">{detail}</p>
    <div className="pt-6 border-t border-slate-50 flex justify-between items-center">
      <span className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Telemetry Spec</span>
      <span className="text-xs font-black text-slate-900 font-mono">{metric}</span>
    </div>
  </motion.div>
);

export default PlatformHealth;